/** Aspect options for image crop (toolbar dropdown). */
export const IMAGE_CROP_ASPECTS = [
  { id: "custom", label: "Custom", short: "Freeform" },
  { id: "original", label: "Original", short: "Original" },
  { id: "square", label: "Square", short: "1:1" },
  { id: "portrait", label: "Portrait", short: "3:4" },
  { id: "landscape", label: "Landscape", short: "4:3" },
];

export const DEFAULT_CROP_RECT = { x: 0, y: 0, w: 1, h: 1 };

export function normalizeCropRect(rect) {
  if (!rect || typeof rect !== "object") return { ...DEFAULT_CROP_RECT };
  let x = Math.max(0, Math.min(1, Number(rect.x) || 0));
  let y = Math.max(0, Math.min(1, Number(rect.y) || 0));
  let w = Math.max(0.05, Math.min(1, Number(rect.w) || 1));
  let h = Math.max(0.05, Math.min(1, Number(rect.h) || 1));
  if (x + w > 1) x = Math.max(0, 1 - w);
  if (y + h > 1) y = Math.max(0, 1 - h);
  w = Math.min(w, 1 - x);
  h = Math.min(h, 1 - y);
  return { x, y, w, h };
}

export function isFullCropRect(rect) {
  const r = normalizeCropRect(rect);
  return r.x <= 0.001 && r.y <= 0.001 && r.w >= 0.998 && r.h >= 0.998;
}

/** Numeric pixel aspect (w/h), or null for freeform custom. */
export function resolveCropAspectRatio(
  aspectId = "custom",
  naturalWidth = 0,
  naturalHeight = 0
) {
  const id = aspectId || "custom";
  if (id === "custom") return null;
  if (id === "square") return 1;
  if (id === "portrait") return 3 / 4;
  if (id === "landscape") return 4 / 3;
  if (id === "original") {
    const nw = Number(naturalWidth) || 0;
    const nh = Number(naturalHeight) || 0;
    if (nw > 0 && nh > 0) return nw / nh;
    return 1;
  }
  return null;
}

/**
 * Convert a pixel aspect (output w/h) into normalized crop w/h.
 * Unit crop space maps to the full bitmap, which already has sourceAspect.
 */
export function pixelAspectToNormalized(
  pixelAspect,
  naturalWidth = 0,
  naturalHeight = 0
) {
  if (!pixelAspect || !(pixelAspect > 0)) return null;
  const nw = Math.max(1, Number(naturalWidth) || 1);
  const nh = Math.max(1, Number(naturalHeight) || 1);
  return pixelAspect / (nw / nh);
}

/** Largest centered rect of the given normalized aspect (w/h) in the unit square. */
export function fitCropRectToAspect(aspectRatio, fromRect = null) {
  if (!aspectRatio || !(aspectRatio > 0)) {
    return normalizeCropRect(fromRect || DEFAULT_CROP_RECT);
  }
  let w;
  let h;
  if (aspectRatio >= 1) {
    w = 1;
    h = 1 / aspectRatio;
    if (h > 1) {
      h = 1;
      w = aspectRatio;
    }
  } else {
    h = 1;
    w = aspectRatio;
    if (w > 1) {
      w = 1;
      h = 1 / aspectRatio;
    }
  }
  // Max fit: touch at least one pair of opposite edges of the logo.
  const base = fromRect ? normalizeCropRect(fromRect) : null;
  let x = base ? base.x + (base.w - w) / 2 : (1 - w) / 2;
  let y = base ? base.y + (base.h - h) / 2 : (1 - h) / 2;
  return normalizeCropRect({ x, y, w, h });
}

/**
 * Fit crop window for a toolbar aspect id using the logo's natural size.
 * Original → full image; Square/Portrait/Landscape → true pixel aspect.
 */
export function fitCropRectForAspectId(
  aspectId = "custom",
  naturalWidth = 0,
  naturalHeight = 0,
  fromRect = null
) {
  const id = aspectId || "custom";
  if (id === "custom") {
    return normalizeCropRect(fromRect || DEFAULT_CROP_RECT);
  }
  if (id === "original") {
    return { ...DEFAULT_CROP_RECT };
  }
  const pixelRatio = resolveCropAspectRatio(id, naturalWidth, naturalHeight);
  const normRatio = pixelAspectToNormalized(
    pixelRatio,
    naturalWidth,
    naturalHeight
  );
  return fitCropRectToAspect(normRatio, fromRect);
}

/**
 * Resize crop rect from a corner handle.
 * dx/dy are normalized deltas in logo space (0–1).
 */
export function resizeCropRectFromHandle(
  startRect,
  handle,
  dx,
  dy,
  aspectRatio = null
) {
  const s = normalizeCropRect(startRect);
  let left = s.x;
  let top = s.y;
  let right = s.x + s.w;
  let bottom = s.y + s.h;

  if (handle.includes("w")) left = s.x + dx;
  if (handle.includes("e")) right = s.x + s.w + dx;
  if (handle.includes("n")) top = s.y + dy;
  if (handle.includes("s")) bottom = s.y + s.h + dy;

  left = Math.max(0, Math.min(1, left));
  right = Math.max(0, Math.min(1, right));
  top = Math.max(0, Math.min(1, top));
  bottom = Math.max(0, Math.min(1, bottom));

  if (right - left < 0.05) {
    if (handle.includes("w")) left = right - 0.05;
    else right = left + 0.05;
  }
  if (bottom - top < 0.05) {
    if (handle.includes("n")) top = bottom - 0.05;
    else bottom = top + 0.05;
  }

  left = Math.max(0, left);
  right = Math.min(1, right);
  top = Math.max(0, top);
  bottom = Math.min(1, bottom);

  let w = right - left;
  let h = bottom - top;
  let x = left;
  let y = top;

  if (aspectRatio && aspectRatio > 0) {
    const targetH = w / aspectRatio;
    const targetW = h * aspectRatio;
    const fromW = handle.includes("e") || handle.includes("w");
    const fromH = handle.includes("n") || handle.includes("s");

    if (fromW && !fromH) {
      h = targetH;
      if (handle.includes("n")) y = bottom - h;
      else y = top;
    } else if (fromH && !fromW) {
      w = targetW;
      if (handle.includes("w")) x = right - w;
      else x = left;
    } else {
      // Corner: use the dominant delta axis.
      if (Math.abs(dx) * aspectRatio >= Math.abs(dy)) {
        h = targetH;
        if (handle.includes("n")) y = bottom - h;
        else y = top;
      } else {
        w = targetW;
        if (handle.includes("w")) x = right - w;
        else x = left;
      }
    }

    if (x < 0) {
      w += x;
      x = 0;
      h = w / aspectRatio;
      if (handle.includes("n")) y = bottom - h;
    }
    if (y < 0) {
      h += y;
      y = 0;
      w = h * aspectRatio;
      if (handle.includes("w")) x = right - w;
    }
    if (x + w > 1) {
      w = 1 - x;
      h = w / aspectRatio;
      if (handle.includes("n")) y = bottom - h;
    }
    if (y + h > 1) {
      h = 1 - y;
      w = h * aspectRatio;
      if (handle.includes("w")) x = right - w;
    }
  }

  return normalizeCropRect({ x, y, w, h });
}

export function moveCropRect(startRect, dx, dy) {
  const s = normalizeCropRect(startRect);
  return normalizeCropRect({
    x: s.x + dx,
    y: s.y + dy,
    w: s.w,
    h: s.h,
  });
}

/** Image placement on the crop host (may extend outside 0–1 when panning/zooming). */
export function normalizeImageFrame(frame) {
  if (!frame || typeof frame !== "object") {
    return { x: 0, y: 0, w: 1, h: 1 };
  }
  return {
    x: Number(frame.x) || 0,
    y: Number(frame.y) || 0,
    w: Math.max(0.08, Number(frame.w) || 1),
    h: Math.max(0.08, Number(frame.h) || 1),
  };
}

/** Host-space crop window (not clamped to unit square — nest inside image instead). */
export function normalizeHostCropFrame(frame) {
  if (!frame || typeof frame !== "object") {
    return { ...DEFAULT_CROP_RECT };
  }
  return {
    x: Number(frame.x) || 0,
    y: Number(frame.y) || 0,
    w: Math.max(0.05, Number(frame.w) || 1),
    h: Math.max(0.05, Number(frame.h) || 1),
  };
}

export const DEFAULT_IMAGE_FRAME = { x: 0, y: 0, w: 1, h: 1 };

/** Map host crop + image frames to a source-space crop rect (export / committed state). */
export function hostFramesToSourceRect(cropFrame, imageFrame = DEFAULT_IMAGE_FRAME) {
  const img = normalizeImageFrame(imageFrame);
  const crop = normalizeHostCropFrame(cropFrame);
  return normalizeCropRect({
    x: (crop.x - img.x) / img.w,
    y: (crop.y - img.y) / img.h,
    w: crop.w / img.w,
    h: crop.h / img.h,
  });
}

/** Map a source crop rect to host crop frame (image frame defaults to full host). */
export function sourceRectToHostCropFrame(
  sourceRect,
  imageFrame = DEFAULT_IMAGE_FRAME
) {
  const src = normalizeCropRect(sourceRect);
  const img = normalizeImageFrame(imageFrame);
  return normalizeHostCropFrame({
    x: img.x + src.x * img.w,
    y: img.y + src.y * img.h,
    w: src.w * img.w,
    h: src.h * img.h,
  });
}

/**
 * Keep crop fully inside the logo/image frame (they must never separate).
 */
export function clampCropInsideImage(cropFrame, imageFrame) {
  const img = normalizeImageFrame(imageFrame);
  let { x, y, w, h } = normalizeHostCropFrame(cropFrame);
  w = Math.min(w, img.w);
  h = Math.min(h, img.h);
  x = Math.max(img.x, Math.min(img.x + img.w - w, x));
  y = Math.max(img.y, Math.min(img.y + img.h - h, y));
  return normalizeHostCropFrame({ x, y, w, h });
}

/**
 * Keep logo covering the crop window (pan/zoom can't leave empty crop edges).
 */
export function clampImageToCoverCrop(imageFrame, cropFrame) {
  const crop = normalizeHostCropFrame(cropFrame);
  let { x, y, w, h } = normalizeImageFrame(imageFrame);
  w = Math.max(w, crop.w);
  h = Math.max(h, crop.h);
  // Image left edge ≤ crop left; image right edge ≥ crop right
  x = Math.min(crop.x, Math.max(crop.x + crop.w - w, x));
  y = Math.min(crop.y, Math.max(crop.y + crop.h - h, y));
  return normalizeImageFrame({ x, y, w, h });
}

export function moveImageFrame(startFrame, dx, dy, cropFrame = null) {
  const f = normalizeImageFrame(startFrame);
  const next = normalizeImageFrame({
    x: f.x + dx,
    y: f.y + dy,
    w: f.w,
    h: f.h,
  });
  return cropFrame ? clampImageToCoverCrop(next, cropFrame) : next;
}

export function moveCropRectInsideImage(startRect, dx, dy, imageFrame) {
  const s = normalizeHostCropFrame(startRect);
  return clampCropInsideImage(
    { x: s.x + dx, y: s.y + dy, w: s.w, h: s.h },
    imageFrame
  );
}

/** Uniform scale from a corner handle (zoom image under a fixed crop window). */
export function resizeImageFrameFromHandle(
  startFrame,
  handle,
  dx,
  dy,
  cropFrame = null
) {
  const s = normalizeImageFrame(startFrame);
  let scale = 1;
  if (handle === "se") scale = 1 + (dx / s.w + dy / s.h) / 2;
  else if (handle === "sw") scale = 1 + (-dx / s.w + dy / s.h) / 2;
  else if (handle === "ne") scale = 1 + (dx / s.w - dy / s.h) / 2;
  else if (handle === "nw") scale = 1 + (-dx / s.w - dy / s.h) / 2;
  scale = Math.max(0.25, Math.min(4, scale));
  const w = s.w * scale;
  const h = s.h * scale;
  let x = s.x;
  let y = s.y;
  if (handle.includes("w")) x = s.x + s.w - w;
  if (handle.includes("n")) y = s.y + s.h - h;
  const next = normalizeImageFrame({ x, y, w, h });
  return cropFrame ? clampImageToCoverCrop(next, cropFrame) : next;
}

/** Resize crop with optional aspect lock, clamped inside the logo frame. */
export function resizeCropRectInsideImage(
  startRect,
  handle,
  dx,
  dy,
  aspectRatio,
  imageFrame
) {
  const img = normalizeImageFrame(imageFrame);
  // Resize in image-local space so aspect math stays correct when image ≠ unit square.
  const local = {
    x: (startRect.x - img.x) / img.w,
    y: (startRect.y - img.y) / img.h,
    w: startRect.w / img.w,
    h: startRect.h / img.h,
  };
  const nextLocal = resizeCropRectFromHandle(
    local,
    handle,
    dx / img.w,
    dy / img.h,
    aspectRatio
  );
  return clampCropInsideImage(
    {
      x: img.x + nextLocal.x * img.w,
      y: img.y + nextLocal.y * img.h,
      w: nextLocal.w * img.w,
      h: nextLocal.h * img.h,
    },
    img
  );
}

/** Normalized w/h aspect lock for crop handles on the host (matches source when image is full). */
export function hostCropAspectRatio(cropAspect, naturalWidth = 0, naturalHeight = 0) {
  if (cropAspect === "custom") return null;
  const pixelAspect = resolveCropAspectRatio(
    cropAspect,
    naturalWidth,
    naturalHeight
  );
  return pixelAspectToNormalized(pixelAspect, naturalWidth, naturalHeight);
}

/** Box position for dim-outside-crop overlay (preview UI). */
export function cropDimOverlayBoxStyle(cropRect) {
  const r = normalizeCropRect(cropRect);
  return {
    left: `${r.x * 100}%`,
    top: `${r.y * 100}%`,
    width: `${r.w * 100}%`,
    height: `${r.h * 100}%`,
  };
}

export const CROP_OUTSIDE_DIM_SHADOW = "0 0 0 9999px rgba(0, 0, 0, 0.48)";

/** CSS for painting the cropped region inside a mark box sized to the crop. */
export function cropImageObjectStyle(cropRect) {
  const r = normalizeCropRect(cropRect);
  if (isFullCropRect(r)) {
    return {
      width: "100%",
      height: "100%",
      objectFit: "contain",
      display: "block",
    };
  }
  return {
    position: "absolute",
    width: `${100 / r.w}%`,
    height: `${100 / r.h}%`,
    left: `${(-r.x / r.w) * 100}%`,
    top: `${(-r.y / r.h) * 100}%`,
    maxWidth: "none",
    objectFit: "fill",
    display: "block",
  };
}
